import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthContext } from '../../hooks/useAuthContext';
import { RiUserAddFill } from 'react-icons/ri'; // Import the RiUserAddFill icon
import roles from '../../data/roles'; // Import the roles data
import { useSnackbar } from 'notistack'; // Import enqueueSnackbar
import { useTranslation } from 'react-i18next'; // Import translation hook
import i18n from '../../i18n'; // Import i18n for language detection
import '../../style/OrganizationPage.css'; // Import the CSS file

const OrganizationPage = () => {
    const { orgId } = useParams(); // Get the organization ID from the URL
    const { user } = useAuthContext(); // Get the authenticated user
    const navigate = useNavigate(); // For navigation
    const { enqueueSnackbar } = useSnackbar(); // Initialize enqueueSnackbar
    const { t } = useTranslation(); // Translation function
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editedRoles, setEditedRoles] = useState({}); // Track role changes
    const [userRole, setUserRole] = useState(null); // Track the user's role in the organization

    useEffect(() => {
        const fetchMembers = async () => {
            setLoading(true);
            setError(null);

            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }

                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/organizations/${orgId}/members`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                setMembers(response.data);

                // Extract the user's role for the current organization
                const userOrgRole = response.data.find(
                    (member) => member.userId._id === user._id
                )?.role;

                setUserRole(userOrgRole || null); // Set the user's role
            } catch (err) {
                enqueueSnackbar(err.response?.data?.error || t('organizationPage.fetchError'), { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchMembers();
    }, [orgId, user, t, enqueueSnackbar]);

    const handleRoleChange = (memberId, newRole) => {
        setEditedRoles((prev) => ({
            ...prev,
            [memberId]: newRole,
        }));
    };

    const saveChanges = async () => {
        setLoading(true);
        setError(null);

        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const promises = Object.entries(editedRoles).map(([memberId, newRole]) =>
                axios.put(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/organizations/${orgId}/members/${memberId}/role`,
                    { role: newRole },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                )
            );

            await Promise.all(promises);
            enqueueSnackbar(t('organizationPage.rolesUpdated'), { variant: 'success' });
            setEditedRoles({});

            // Fetch updated members to reflect changes
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/organizations/${orgId}/members`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setMembers(response.data);
        } catch (err) {
            enqueueSnackbar(err.response?.data?.error || t('organizationPage.updateError'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const redirectToInvitationPage = () => {
        navigate(`/organizations/${orgId}/invite`);
    };

    const getRoleLabel = (roleValue) => {
        const role = roles.find((r) => r.value === roleValue);
        return role ? role.label[i18n.language] : roleValue; // Directly use the label from roles.js
    };

    if (loading) return <p className="text-dafauna-darkText">{t('organizationPage.loading')}</p>;
    if (error) return <p className="error-message">{error}</p>;

    return (
        <div className="organization-page bg-dafauna-background rounded-xl shadow-md p-8">
            <div className="title-container-organization flex justify-between items-center mb-4">
                <h2 className="text-dafauna-darkText text-2xl font-bold">{t('organizationPage.title')}</h2>
                <button
                    type="button"
                    className="invite-container flex items-center focus:outline-none"
                    onClick={redirectToInvitationPage}
                    aria-label={t('organizationPage.invite')}
                >
                    <span className='invite-text text-dafauna-primary font-medium'>{t('organizationPage.invite')}</span>
                    <RiUserAddFill
                        className="invite-icon text-dafauna-primary hover:text-dafauna-primaryLight cursor-pointer ml-2"
                    />
                </button>
            </div>
            {!loading && members.length > 0 && userRole && (
                <table className="members-table w-full border-collapse">
                    <thead>
                        <tr>
                            <th className="bg-dafauna-primary text-white">{t('organizationPage.name')}</th>
                            <th className="bg-dafauna-primary text-white">{t('organizationPage.email')}</th>
                            <th className="bg-dafauna-primary text-white">{t('organizationPage.role')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {members.map((member) => (
                            <tr key={member.userId._id}>
                                <td className="border border-gray-300 p-2">{member.userId.name}</td>
                                <td className="border border-gray-300 p-2">{member.userId.email}</td>
                                <td className="border border-gray-300 p-2">
                                    {userRole === 'admin' ? (
                                        <select
                                            value={editedRoles[member.userId._id] || member.role}
                                            onChange={(e) =>
                                                handleRoleChange(member.userId._id, e.target.value)
                                            }
                                            className="border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:border-dafauna-primary"
                                        >
                                            {roles.map((role) => (
                                                <option key={role.value} value={role.value}>
                                                    {role.label[i18n.language]} {/* Use the label from roles.js */}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        getRoleLabel(member.role) // Use the label for display
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {userRole === 'admin' && Object.keys(editedRoles).length > 0 && (
                <button
                    className="save-button bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
                    onClick={saveChanges}
                    disabled={loading}
                >
                    {loading ? t('organizationPage.saving') : t('organizationPage.saveChanges')}
                </button>
            )}
        </div>
    );
};

export default OrganizationPage;